import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  MapPin, Plus, Trash2, GripVertical, Save, ArrowLeft,
  Sun, Moon, Utensils, Camera, ChevronDown, ChevronUp, Image
} from 'lucide-react';
import ImageUpload from '../ui/image-upload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ItineraryEditor({ 
  trip, 
  onBack, 
  onSave,
  variant = null,           // For location variant itinerary
  accommodationLevel = null // For accommodation-based itinerary
}) {
  const { token } = useAuth();
  const [itinerary, setItinerary] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);

  // Determine which itinerary to load based on mode
  const getInitialItinerary = () => {
    if (variant) {
      // For variants, use the itinerary directly from the variant prop
      return variant?.itinerary || [];
    }
    if (accommodationLevel) {
      // Check if we're editing a traveler tier within the accommodation level
      if (accommodationLevel._tierEdit) {
        return accommodationLevel._tierEdit.itinerary || [];
      }
      // For accommodation levels, use the itinerary directly from the level prop
      return accommodationLevel?.itinerary || [];
    }
    return trip?.itinerary || [];
  };

  // Load itinerary only once when component mounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const existingItinerary = getInitialItinerary();
    if (existingItinerary && existingItinerary.length > 0) {
      // Deep clone to avoid mutating props
      setItinerary(JSON.parse(JSON.stringify(existingItinerary)));
    } else {
      // Initialize with empty days based on trip duration
      const days = trip?.duration_days || 7;
      setItinerary(Array.from({ length: days }, (_, i) => ({
        day: i + 1,
        title: `Day ${i + 1}`,
        description: '',
        image: '',
        activities: [],
        meals: { breakfast: '', lunch: '', dinner: '' },
        accommodation: '',
        highlights: []
      })));
    }
  }, []); // Empty dependency array - only run once on mount

  const handleSave = async () => {
    // Validate required data
    const tripId = trip?.id;
    if (!tripId) {
      toast.error('Error: Trip ID is missing');
      return;
    }
    
    if (!itinerary || itinerary.length === 0) {
      toast.error('Error: No itinerary data to save');
      return;
    }

    setSaving(true);
    
    // Determine endpoint and payload based on mode
    let endpoint;
    let payload;
    let contextName;
    
    if (variant) {
      if (!variant.id) {
        toast.error('Error: Variant ID is missing');
        setSaving(false);
        return;
      }
      endpoint = `${API}/trips/${tripId}/variant-itinerary`;
      payload = { variant_id: variant.id, itinerary: itinerary };
      contextName = variant.name;
    } else if (accommodationLevel) {
      if (!accommodationLevel.id) {
        toast.error('Error: Accommodation Level ID is missing');
        setSaving(false);
        return;
      }
      endpoint = `${API}/trips/${tripId}/accommodation-itinerary`;
      
      // Check if we're editing a traveler tier within the accommodation level
      if (accommodationLevel._tierEdit) {
        payload = { 
          accommodation_level_id: accommodationLevel.id, 
          tier_id: accommodationLevel._tierEdit.id,
          itinerary: itinerary 
        };
        contextName = `${accommodationLevel.name} (${accommodationLevel._tierEdit.min_travelers}-${accommodationLevel._tierEdit.max_travelers} travelers)`;
      } else {
        payload = { accommodation_level_id: accommodationLevel.id, itinerary: itinerary };
        contextName = accommodationLevel.name;
      }
    } else {
      endpoint = `${API}/trips/${tripId}/itinerary`;
      payload = { itinerary: itinerary };
      contextName = 'Main';
    }

    try {
      console.log(`Saving ${contextName} itinerary (${itinerary.length} days) to: ${endpoint}`);
      
      const response = await axios.put(endpoint, payload, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Save response:', response.data);
      toast.success(`${contextName} itinerary saved!`);
      
      // Wait a moment before triggering refresh to ensure DB write completes
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (onSave) {
        onSave();
      }
    } catch (err) {
      console.error('Save error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save';
      toast.error(`Save failed: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };
  
  // Get display title based on mode
  const getEditorTitle = () => {
    if (variant) return `Edit Itinerary: ${variant.name}`;
    if (accommodationLevel) {
      if (accommodationLevel._tierEdit) {
        return `Edit Itinerary: ${accommodationLevel.name} (${accommodationLevel._tierEdit.min_travelers}-${accommodationLevel._tierEdit.max_travelers} travelers)`;
      }
      return `Edit Itinerary: ${accommodationLevel.name}`;
    }
    return 'Edit Itinerary';
  };
  
  const getEditorSubtitle = () => {
    if (variant) return `Location variant for ${trip?.title}`;
    if (accommodationLevel) {
      if (accommodationLevel._tierEdit) {
        return `Traveler tier for ${accommodationLevel.name} accommodation - ${trip?.title}`;
      }
      return `${accommodationLevel.name} accommodation for ${trip?.title}`;
    }
    return trip?.title;
  };

  const updateDay = (dayIndex, field, value) => {
    setItinerary(prev => prev.map((day, i) => 
      i === dayIndex ? { ...day, [field]: value } : day
    ));
  };

  const updateMeal = (dayIndex, mealType, value) => {
    setItinerary(prev => prev.map((day, i) => 
      i === dayIndex ? { ...day, meals: { ...day.meals, [mealType]: value } } : day
    ));
  };

  const addActivity = (dayIndex) => {
    setItinerary(prev => prev.map((day, i) => 
      i === dayIndex ? { 
        ...day, 
        activities: [...(day.activities || []), { time: '', description: '', location: '' }] 
      } : day
    ));
  };

  const updateActivity = (dayIndex, actIndex, field, value) => {
    setItinerary(prev => prev.map((day, i) => 
      i === dayIndex ? {
        ...day,
        activities: day.activities.map((act, j) => 
          j === actIndex ? { ...act, [field]: value } : act
        )
      } : day
    ));
  };

  const removeActivity = (dayIndex, actIndex) => {
    setItinerary(prev => prev.map((day, i) => 
      i === dayIndex ? {
        ...day,
        activities: day.activities.filter((_, j) => j !== actIndex)
      } : day
    ));
  };

  const addHighlight = (dayIndex) => {
    setItinerary(prev => prev.map((day, i) => 
      i === dayIndex ? { 
        ...day, 
        highlights: [...(day.highlights || []), ''] 
      } : day
    ));
  };

  const updateHighlight = (dayIndex, hlIndex, value) => {
    setItinerary(prev => prev.map((day, i) => 
      i === dayIndex ? {
        ...day,
        highlights: day.highlights.map((hl, j) => j === hlIndex ? value : hl)
      } : day
    ));
  };

  const removeHighlight = (dayIndex, hlIndex) => {
    setItinerary(prev => prev.map((day, i) => 
      i === dayIndex ? {
        ...day,
        highlights: day.highlights.filter((_, j) => j !== hlIndex)
      } : day
    ));
  };

  const addDay = () => {
    const newDayNum = itinerary.length + 1;
    setItinerary(prev => [...prev, {
      day: newDayNum,
      title: `Day ${newDayNum}`,
      description: '',
      image: '',
      activities: [],
      meals: { breakfast: '', lunch: '', dinner: '' },
      accommodation: '',
      highlights: []
    }]);
  };

  const removeDay = (dayIndex) => {
    // Remove the day and renumber remaining days
    setItinerary(prev => prev.filter((_, i) => i !== dayIndex).map((day, i) => ({
      ...day,
      day: i + 1
    })));
    toast.success('Day removed from itinerary');
  };

  return (
    <div className="space-y-6" data-testid="itinerary-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold">{getEditorTitle()}</h2>
            <p className="text-muted-foreground">{getEditorSubtitle()}</p>
            {(variant || accommodationLevel) && (
              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-secondary/10 text-secondary">
                {variant ? '📍 Location Variant' : '🏨 Accommodation Level'}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={addDay}>
            <Plus className="h-4 w-4 mr-2" />
            Add Day
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Itinerary'}
          </Button>
        </div>
      </div>

      {/* Days */}
      <div className="space-y-4">
        {itinerary.map((day, dayIndex) => (
          <Card key={dayIndex} className="overflow-hidden">
            <CardHeader 
              className="cursor-pointer bg-muted/30 hover:bg-muted/50 transition-colors"
              onClick={() => setExpandedDay(expandedDay === dayIndex ? null : dayIndex)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary text-white flex items-center justify-center font-bold">
                    {day.day}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{day.title || `Day ${day.day}`}</CardTitle>
                    {day.description && (
                      <p className="text-sm text-muted-foreground line-clamp-1">{day.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); removeDay(dayIndex); }}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  {expandedDay === dayIndex ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CardHeader>

            {expandedDay === dayIndex && (
              <CardContent className="pt-6 space-y-6">
                {/* Day Image */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Image className="h-4 w-4" />
                    Day Image
                  </Label>
                  <ImageUpload
                    value={day.image || ''}
                    onChange={(url) => updateDay(dayIndex, 'image', url)}
                    token={token}
                    placeholder="https://example.com/image.jpg"
                    previewClassName="w-48 h-32"
                  />
                </div>

                {/* Basic Info */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Day Title</Label>
                    <Input
                      value={day.title}
                      onChange={(e) => updateDay(dayIndex, 'title', e.target.value)}
                      placeholder="Arrival in Cairo"
                    />
                  </div>
                  <div>
                    <Label>Accommodation</Label>
                    <Input
                      value={day.accommodation || ''}
                      onChange={(e) => updateDay(dayIndex, 'accommodation', e.target.value)}
                      placeholder="Marriott Mena House"
                    />
                  </div>
                </div>

                <div>
                  <Label>Day Description</Label>
                  <Textarea
                    value={day.description || ''}
                    onChange={(e) => updateDay(dayIndex, 'description', e.target.value)}
                    placeholder="Describe what happens on this day..."
                    rows={3}
                  />
                </div>

                {/* Meals */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Utensils className="h-4 w-4" />
                    Meals
                  </Label>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Breakfast</Label>
                      <Input
                        value={day.meals?.breakfast || ''}
                        onChange={(e) => updateMeal(dayIndex, 'breakfast', e.target.value)}
                        placeholder="Hotel breakfast"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Lunch</Label>
                      <Input
                        value={day.meals?.lunch || ''}
                        onChange={(e) => updateMeal(dayIndex, 'lunch', e.target.value)}
                        placeholder="Local restaurant"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Dinner</Label>
                      <Input
                        value={day.meals?.dinner || ''}
                        onChange={(e) => updateMeal(dayIndex, 'dinner', e.target.value)}
                        placeholder="Group dinner"
                      />
                    </div>
                  </div>
                </div>

                {/* Activities */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <Camera className="h-4 w-4" />
                      Activities
                    </Label>
                    <Button variant="outline" size="sm" onClick={() => addActivity(dayIndex)}>
                      <Plus className="h-3 w-3 mr-1" />
                      Add Activity
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {(day.activities || []).map((activity, actIndex) => (
                      <div key={actIndex} className="flex gap-3 items-start p-3 bg-muted/30 rounded-lg">
                        <div className="flex-shrink-0 w-20">
                          <Input
                            value={activity.time || ''}
                            onChange={(e) => updateActivity(dayIndex, actIndex, 'time', e.target.value)}
                            placeholder="9:00 AM"
                            className="text-sm"
                          />
                        </div>
                        <div className="flex-1 space-y-2">
                          <Input
                            value={activity.description || ''}
                            onChange={(e) => updateActivity(dayIndex, actIndex, 'description', e.target.value)}
                            placeholder="Activity description"
                          />
                          <Input
                            value={activity.location || ''}
                            onChange={(e) => updateActivity(dayIndex, actIndex, 'location', e.target.value)}
                            placeholder="Location (optional)"
                            className="text-sm"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeActivity(dayIndex, actIndex)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    {(!day.activities || day.activities.length === 0) && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No activities added yet
                      </p>
                    )}
                  </div>
                </div>

                {/* Highlights */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <Sun className="h-4 w-4" />
                      Day Highlights
                    </Label>
                    <Button variant="outline" size="sm" onClick={() => addHighlight(dayIndex)}>
                      <Plus className="h-3 w-3 mr-1" />
                      Add Highlight
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(day.highlights || []).map((highlight, hlIndex) => (
                      <div key={hlIndex} className="flex gap-2">
                        <Input
                          value={highlight}
                          onChange={(e) => updateHighlight(dayIndex, hlIndex, e.target.value)}
                          placeholder="Visit the Pyramids of Giza"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeHighlight(dayIndex, hlIndex)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {itinerary.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <MapPin className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-muted-foreground">No itinerary days yet</p>
            <Button className="mt-4" onClick={addDay}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Day
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
